import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  DragHandle as DragHandleIcon,
  Edit as EditIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { processModuleApi } from 'src/api/process-module';
import { useConfirm } from 'src/hooks/use-confirm';
import { useProcessModules } from 'src/hooks/use-process-modules';
import logger from 'src/log/log';
import { paths } from 'src/paths';

interface Form {
  id: string;
  name: string;
  description: string;
  sequence: number;
  fields: any[];
}

const ModuleFormList = () => {
  const navigate = useNavigate();
  const { organizationId, moduleId } = useParams();
  const [forms, setForms] = useState<Form[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isManager, setIsManager] = useState(false);
  const { confirm, ConfirmationDialog } = useConfirm();
  const modules = useProcessModules();
  const currentModule = modules.find((m) => m.id === moduleId);

  const fetchForms = useCallback(async () => {
    if (!moduleId) return;

    try {
      // First get the process module to check permissions
      const processModule = await processModuleApi.getProcessModule(moduleId);
      if (!processModule?.managerGroups?.length) {
        logger.error('Process module has no manager groups');
        toast.error('Failed to get organization information');
        return;
      }

      setIsManager(true); // TODO: Add proper permission check here

      const result = await processModuleApi.getFormsForProcessModule();
      if (!result.data?.listForms?.items) {
        logger.error('No forms found');
        return;
      }

      const formsList = result.data.listForms.items
        .filter((form): form is NonNullable<typeof form> => Boolean(form))
        .filter((form) => form.processModuleID === moduleId)
        .map((form) => ({
          id: form.id,
          name: form.name,
          description: form.description || '',
          sequence: form.sequence || 0,
          fields: JSON.parse(form.fields || '[]'),
        }))
        .sort((a, b) => a.sequence - b.sequence);

      setForms(formsList);
    } catch (error) {
      logger.error('Error fetching forms:', error);
      toast.error('Failed to load forms');
    } finally {
      setIsLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleDragEnd = useCallback(
    async (result: any) => {
      if (!result.destination || !moduleId) return;

      const reorderedForms = Array.from(forms);
      const [removed] = reorderedForms.splice(result.source.index, 1);
      reorderedForms.splice(result.destination.index, 0, removed);

      // Update sequences
      const updatedForms = reorderedForms.map((form, index) => ({
        ...form,
        sequence: index,
      }));

      setForms(updatedForms);

      try {
        // Update sequences in backend
        await processModuleApi.batchReorderForms(
          organizationId!,
          moduleId,
          updatedForms.map((form) => ({ id: form.id, sequence: form.sequence })),
        );
        toast.success('Form order updated successfully');
      } catch (error) {
        logger.error('Error updating form sequences:', error);
        toast.error('Failed to update form order');
        // Revert to original order
        fetchForms();
      }
    },
    [forms, moduleId, fetchForms, organizationId],
  );

  const handleEditForm = useCallback(
    (formId: string) => {
      navigate(paths.settings.formBuilder.formEdit(organizationId || '', moduleId || '', formId));
    },
    [moduleId, navigate, organizationId],
  );

  const handleCreateForm = useCallback(() => {
    navigate(paths.settings.formBuilder.formEdit(organizationId || '', moduleId || '', 'new'));
  }, [moduleId, navigate, organizationId]);

  const handleDeleteForm = useCallback(
    async (formId: string, formName: string) => {
      if (!moduleId) return;

      const confirmed = await confirm({
        title: 'Delete Form',
        description: `Are you sure you want to delete "${formName}"? You can choose to keep the fields for future use.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmColor: 'error',
        checkboxLabel: 'Keep fields for future use',
        defaultChecked: false,
      });

      if (!confirmed || typeof confirmed === 'boolean') return;

      try {
        await processModuleApi.deleteForm(formId, organizationId!, moduleId!, confirmed.checked);
        toast.success('Form deleted successfully');
        fetchForms();
      } catch (error) {
        logger.error('Error deleting form:', error);
        toast.error('Failed to delete form');
      }
    },
    [moduleId, confirm, fetchForms, organizationId],
  );

  const handleBack = () => {
    navigate(paths.settings.formBuilder.moduleList(organizationId || ''));
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <LinearProgress />
          <Typography sx={{ mt: 2 }}>Loading forms...</Typography>
        </Box>
      </Container>
    );
  }

  if (!isManager) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Typography variant="h6" color="error">
            You don't have permission to manage forms
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      {ConfirmationDialog}
      <Container maxWidth="lg">
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleBack();
            }}
          >
            Process Modules
          </Link>
          <Typography color="text.primary">{currentModule?.name}</Typography>
        </Breadcrumbs>
        <Box sx={{ py: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Box>
                    <Typography variant="h4">Forms for {currentModule?.name}</Typography>
                    <Typography color="text.secondary">Manage and organize forms for this process module</Typography>
                  </Box>
                </Box>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreateForm}>
                  Create New Form
                </Button>
              </Stack>

              <Divider />

              {forms.length === 0 ? (
                <Box
                  sx={{
                    py: 10,
                    textAlign: 'center',
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Forms Yet
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Create your first form to get started
                  </Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreateForm}>
                    Create Form
                  </Button>
                </Box>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="forms">
                    {(provided) => (
                      <List {...provided.droppableProps} ref={provided.innerRef} sx={{ width: '100%' }}>
                        {forms.map((form, index) => (
                          <Draggable key={form.id} draggableId={form.id} index={index}>
                            {(provided) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                divider
                                sx={{
                                  mb: 1,
                                  bgcolor: 'background.paper',
                                  borderRadius: 1,
                                  boxShadow: 1,
                                }}
                              >
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                  <Tooltip title="Drag to reorder">
                                    <IconButton {...provided.dragHandleProps} size="small">
                                      <DragHandleIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6">{form.name}</Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                    >
                                      {form.description}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ mt: 1, display: 'block' }}
                                    >
                                      {form.fields.length} fields
                                    </Typography>
                                  </Box>
                                  <Stack direction="row" spacing={1}>
                                    <Tooltip title="Edit form">
                                      <IconButton onClick={() => handleEditForm(form.id)} color="primary">
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete form">
                                      <IconButton onClick={() => handleDeleteForm(form.id, form.name)} color="error">
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Stack>
                              </ListItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Stack>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default ModuleFormList;
